import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Form, Col, Row, Button, Spinner, Badge, Alert, ProgressBar,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faToolbox,
  faFileLines,
  faCheckDouble,
  faLocationDot,
  faRocket,
  faStop,
} from "@fortawesome/free-solid-svg-icons";
import { faGoogleDrive } from "@fortawesome/free-brands-svg-icons";
import { toast } from "react-toastify";
import Utils from "../../configs/Utils";
import Enums from "../../configs/Enums";
import PageHeader from "../../Components/Common/PageHeader";
import "./style.scss";

const STORAGE_KEY = "tool_support_selected_sheet";
const BATCH_SIZE = 20;

const ToolSupportPage = () => {
  const [sheetsList, setSheetsList] = useState([]);
  const [selectedSheetId, setSelectedSheetId] = useState(
    () => localStorage.getItem(STORAGE_KEY) || ""
  );
  const [loadingSheets, setLoadingSheets] = useState(true);

  // Validation process state
  const [validating, setValidating] = useState(false);
  const [processStep, setProcessStep] = useState(""); // init | validating | writing | done
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [validationResult, setValidationResult] = useState(null);
  const cancelRef = useRef(false);

  useEffect(() => {
    Utils.setPageTitle("Tool Support");
  }, []);

  // Load saved sheets options from server
  useEffect(() => {
    const fetchSheets = async () => {
      try {
        const response = await Utils._apiRequest({
          path: Enums.endpoints.options.LIST,
          type: "GET",
        });
        const options = response.data || [];
        const sheetsOptions = options.filter(
          (opt) => opt.group === "tiktok_lising_sheets"
        );
        setSheetsList(sheetsOptions);

        if (selectedSheetId && sheetsOptions.length) {
          const exists = sheetsOptions.some((s) => s.key === selectedSheetId);
          if (!exists) {
            setSelectedSheetId("");
            localStorage.removeItem(STORAGE_KEY);
          }
        }
      } catch (error) {
        console.log("Failed to fetch sheets options:", error.message);
      } finally {
        setLoadingSheets(false);
      }
    };

    fetchSheets();
  }, []);

  const handleSheetChange = (e) => {
    const value = e.target.value;
    setSelectedSheetId(value);
    setValidationResult(null);
    setProcessStep("");
    if (value) {
      localStorage.setItem(STORAGE_KEY, value);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  const handlePickSheet = useCallback(async () => {
    const refreshResponse = await Utils._apiRequest({
      path: Enums.endpoints.google.oauth2Refresh,
      type: "GET",
    });

    if (!refreshResponse.oath2Token?.access_token) {
      toast.error("Vui lòng kết nối Google Account trước (Settings > Google Drive)");
      return;
    }

    if (!window.google?.picker) {
      toast.error("Google Picker chưa sẵn sàng. Vui lòng thử lại.");
      return;
    }

    const sheetsView = new window.google.picker.View(
      window.google.picker.ViewId.SPREADSHEETS
    );

    const picker = new window.google.picker.PickerBuilder()
      .addView(sheetsView)
      .setOAuthToken(refreshResponse.oath2Token.access_token)
      .setDeveloperKey(process.env.REACT_APP_GOOGLE_API_KEY)
      .setCallback(async (data) => {
        if (data.action === window.google.picker.Action.PICKED) {
          const selected = data.docs[0];
          const payload = {
            key: selected.id,
            group: "tiktok_lising_sheets",
            values: selected,
          };
          const optionResponse = await Utils._apiRequest({
            path: Enums.endpoints.options.SAVE,
            type: "POST",
            payload,
          });
          setSheetsList((prev) => {
            const updated = [...prev, optionResponse];
            return updated.filter(
              (item, index) =>
                updated.findIndex((i) => i.key === item.key) === index
            );
          });
          setSelectedSheetId(selected.id);
          localStorage.setItem(STORAGE_KEY, selected.id);
          toast.success(`Đã chọn: ${selected.name}`);
        }
      })
      .build();
    picker.setVisible(true);
  }, []);

  const handleCancel = () => {
    cancelRef.current = true;
  };

  const handleValidateAddress = async () => {
    if (!selectedSheetId) {
      toast.warning("Vui lòng chọn file Google Sheet trước");
      return;
    }

    cancelRef.current = false;
    setValidating(true);
    setValidationResult(null);
    setProgress({ current: 0, total: 0 });

    try {
      // === Step 1: Init — đọc sheet, lấy danh sách addresses ===
      setProcessStep("init");
      const initRes = await Utils._apiRequest({
        path: Enums.endpoints.toolSupport.VALIDATE_INIT,
        type: "POST",
        payload: { sheetId: selectedSheetId, sheetName: "Jersey-Export" },
      });

      if (initRes.errors) {
        toast.error(initRes.errors);
        return;
      }

      const { addresses, sheetMeta, totalRows } = initRes;
      setProgress({ current: 0, total: addresses.length });

      // === Step 2: Validate theo batch ===
      setProcessStep("validating");
      const allResults = [];

      for (let i = 0; i < addresses.length; i += BATCH_SIZE) {
        if (cancelRef.current) {
          toast.info("Đã huỷ quá trình validate");
          break;
        }

        const batch = addresses.slice(i, i + BATCH_SIZE);
        const batchRes = await Utils._apiRequest({
          path: Enums.endpoints.toolSupport.VALIDATE_BATCH,
          type: "POST",
          payload: { addresses: batch },
        });

        if (batchRes.errors) {
          toast.error(`Batch lỗi: ${batchRes.errors}`);
          // Tiếp tục với batch tiếp theo
        } else {
          allResults.push(...(batchRes.results || []));
        }

        setProgress({ current: Math.min(i + BATCH_SIZE, addresses.length), total: addresses.length });
      }

      if (cancelRef.current) {
        setProcessStep("");
        return;
      }

      // === Step 3: Ghi kết quả vào Google Sheet ===
      setProcessStep("writing");
      const writeRes = await Utils._apiRequest({
        path: Enums.endpoints.toolSupport.VALIDATE_WRITE,
        type: "POST",
        payload: {
          sheetId: selectedSheetId,
          sheetName: "Jersey-Export",
          sheetMeta,
          totalRows,
          results: allResults,
        },
      });

      if (writeRes.errors) {
        toast.error(`Ghi kết quả lỗi: ${writeRes.errors}`);
        return;
      }

      // === Done ===
      setProcessStep("done");
      const summary = {
        total: totalRows,
        validated: allResults.length,
        results: allResults,
      };
      setValidationResult(summary);
      toast.success(`Hoàn thành! Đã validate ${summary.validated}/${summary.total} địa chỉ`);
    } catch (error) {
      toast.error("Có lỗi xảy ra: " + error.message);
    } finally {
      setValidating(false);
    }
  };

  const selectedSheet = sheetsList.find((s) => s.key === selectedSheetId);
  const progressPercent = progress.total > 0
    ? Math.round((progress.current / progress.total) * 100)
    : 0;

  const getStepLabel = () => {
    switch (processStep) {
      case "init": return "Đang đọc dữ liệu từ Google Sheet...";
      case "validating": return `Đang validate địa chỉ (${progress.current}/${progress.total})...`;
      case "writing": return "Đang ghi kết quả vào Google Sheet...";
      case "done": return "Hoàn thành!";
      default: return "";
    }
  };

  const countByStatus = (status) =>
    validationResult?.results?.filter((r) => r.status === status).length || 0;

  return (
    <main className="page-main">
      <div className="main-container container">
        <PageHeader
          title="Tool Support"
          description="Công cụ hỗ trợ quản lý orders từ Google Sheet"
          icon={faToolbox}
        />

        {/* Sheet Selector Section */}
        <section className="tool-support-card mb-4">
          <Row className="g-3 align-items-end">
            <Col md={7} lg={8}>
              <div className="tool-support-copy">
                <span className="tool-support-kicker">Google Sheet</span>
                <h5>Chọn file Google Sheet orders</h5>
                <p>
                  Chọn file Google Sheet chứa dữ liệu orders để sử dụng các công cụ hỗ trợ
                  bên dưới.
                </p>
              </div>
            </Col>
            <Col md={5} lg={4}>
              <Form.Group className="mb-2">
                <Form.Label className="fw-semibold">File Sheet đã lưu</Form.Label>
                <Form.Select
                  value={selectedSheetId}
                  onChange={handleSheetChange}
                  disabled={loadingSheets}
                >
                  <option value="">
                    {loadingSheets
                      ? "Đang tải..."
                      : "-- Chọn file sheet --"}
                  </option>
                  {sheetsList.map((sheet) => (
                    <option key={sheet.key} value={sheet.key}>
                      {sheet.values?.name || sheet.key}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Button
                variant="dark"
                size="sm"
                className="w-100"
                onClick={handlePickSheet}
              >
                <FontAwesomeIcon icon={faGoogleDrive} className="me-2" />
                Chọn từ Google Drive
              </Button>
            </Col>
          </Row>
          {selectedSheet && (
            <div className="tool-support-selected-info mt-3">
              <FontAwesomeIcon icon={faFileLines} className="me-2 text-success" />
              <span>
                Đang sử dụng: <strong>{selectedSheet.values?.name || selectedSheetId}</strong>
              </span>
            </div>
          )}
        </section>

        {/* Fulfillment Tools Section */}
        {selectedSheetId && (
          <section className="tool-support-tools">
            <h5 className="tool-support-section-title mb-3">
              <FontAwesomeIcon icon={faRocket} className="me-2" />
              Fulfillment Tools
            </h5>

            {/* Valid Shipping Address */}
            <div className="tool-support-tool-card mb-3">
              <div className="tool-support-tool-header">
                <div className="tool-support-tool-icon">
                  <FontAwesomeIcon icon={faLocationDot} />
                </div>
                <div className="tool-support-tool-info">
                  <h6>Valid Shipping Address</h6>
                  <p>
                    Đọc dữ liệu từ sheet <Badge bg="secondary">Jersey-Export</Badge>,
                    sử dụng ChatGPT để validate địa chỉ shipping và ghi kết quả
                    vào cột sau "Shipping Method".
                  </p>
                </div>
                <div className="tool-support-tool-action">
                  {validating ? (
                    <Button variant="outline-danger" size="sm" onClick={handleCancel}>
                      <FontAwesomeIcon icon={faStop} className="me-1" />
                      Huỷ
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      onClick={handleValidateAddress}
                      disabled={validating}
                    >
                      <FontAwesomeIcon icon={faCheckDouble} className="me-2" />
                      Validate Address
                    </Button>
                  )}
                </div>
              </div>

              {/* Progress */}
              {validating && processStep && (
                <div className="tool-support-progress mt-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="tool-support-progress-label">
                      <Spinner size="sm" className="me-2" />
                      {getStepLabel()}
                    </span>
                    {processStep === "validating" && (
                      <span className="tool-support-progress-pct">{progressPercent}%</span>
                    )}
                  </div>
                  {processStep === "validating" && (
                    <ProgressBar
                      now={progressPercent}
                      animated
                      striped
                      variant="primary"
                      className="tool-support-progress-bar"
                    />
                  )}
                  {(processStep === "init" || processStep === "writing") && (
                    <ProgressBar animated striped variant="info" now={100} className="tool-support-progress-bar" />
                  )}
                </div>
              )}

              {/* Validation Results */}
              {validationResult && !validating && (
                <div className="tool-support-results mt-3">
                  <Alert variant="success" className="mb-2">
                    <strong>Hoàn thành!</strong> Đã validate{" "}
                    {validationResult.validated}/{validationResult.total} địa chỉ.
                    Kết quả đã được ghi vào Google Sheet.
                  </Alert>
                  <div className="tool-support-results-summary">
                    <Badge bg="success" className="me-2">
                      VALID: {countByStatus("VALID")}
                    </Badge>
                    <Badge bg="warning" text="dark" className="me-2">
                      CORRECTED: {countByStatus("CORRECTED")}
                    </Badge>
                    <Badge bg="danger" className="me-2">
                      INVALID: {countByStatus("INVALID")}
                    </Badge>
                    {countByStatus("ERROR") > 0 && (
                      <Badge bg="secondary" className="me-2">
                        ERROR: {countByStatus("ERROR")}
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Coming Soon */}
            <div className="tool-support-tool-card tool-support-tool-card--disabled mb-3">
              <div className="tool-support-tool-header">
                <div className="tool-support-tool-icon tool-support-tool-icon--muted">
                  <FontAwesomeIcon icon={faRocket} />
                </div>
                <div className="tool-support-tool-info">
                  <h6>
                    New Features{" "}
                    <Badge bg="info" className="ms-2">
                      Coming Soon
                    </Badge>
                  </h6>
                  <p>Các tính năng hỗ trợ mới sẽ được cập nhật tại đây.</p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Empty state when no sheet selected */}
        {!selectedSheetId && !loadingSheets && (
          <div className="tool-support-empty-state text-center">
            <div className="tool-support-empty-state__icon-wrap">
              <FontAwesomeIcon icon={faFileLines} />
            </div>
            <h6>Sẵn sàng hỗ trợ</h6>
            <p className="mb-0">
              Vui lòng chọn file Google Sheet để bắt đầu sử dụng các công cụ hỗ trợ.
            </p>
          </div>
        )}
      </div>
    </main>
  );
};

export default ToolSupportPage;
